import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, stopSession, startSession, renameSession } from "../services/api";
import {
  commitSessionProfile,
  createSessionInContext,
  listContexts,
  listContextSessions,
  resolveProfileCandidate,
} from "../services/contexts";
import type {
  BrowserContextSummary,
  ProfileCandidateResolution,
  ProfileRevisionConflict,
} from "../types/context";
import {
  formatContextStatus,
} from "../types/context";
import type { SessionSummary, DisplayState } from "../types/session";
import {
  isSessionsListResponse,
  isCreateSessionResponse,
  getDisplayState,
} from "../types/session";

import AppPicker from "../components/AppPicker";
import RetirementsPanel from "../components/RetirementsPanel";
import { setRetirement } from "../services/retirements";
import "../styles/ResourceSections.css";

/** Fallback label map — empty; filter chips show raw app name. */
const APP_LABELS: Record<string, string> = {};

// ─── Display state config ────────────────────────────────────────────

const DISPLAY_STATE_CONFIG: Record<
  DisplayState,
  { label: string; color: string; dotColor: string }
> = {
  ongoing: { label: "ONGOING", color: "#22c55e", dotColor: "#22c55e" },
  on: { label: "ON", color: "#eab308", dotColor: "#eab308" },
  off: { label: "OFF", color: "#6b7280", dotColor: "#6b7280" },
  transitioning: { label: "...", color: "#7dd3fc", dotColor: "#7dd3fc" },
};

// ─── Polling interval ─────────────────────────────────────────────────

const POLL_INTERVAL_MS = 5000;

// ─── Helpers ─────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "just now";
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function truncateId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}...` : id;
}

type CreateSessionErrorResponse = Pick<Response, "status" | "json">;

function isUnknownRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isQuotaCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function quotaUsage(body: Record<string, unknown>): { used: number; limit: number } | null {
  if (!isQuotaCount(body.used) || !isQuotaCount(body.limit)) return null;
  return { used: body.used, limit: body.limit };
}

/** Format a closed set of public API errors without trusting arbitrary response fields. */
export function formatCreateSessionError(status: number, body: unknown): string {
  const fallback = `Échec de création de session (HTTP ${status})`;
  if (!isUnknownRecord(body) || typeof body.code !== "string") return fallback;

  if (status === 409 && body.code === "browser_context_not_ready") {
    return "Création refusée : le contexte n’est pas prêt. Une sauvegarde ou une récupération peut encore le bloquer. Consultez l’état de ses sessions et du coffre avant de créer une session.";
  }
  if (status === 429 && body.code === "active_session_quota_exceeded" && body.scope === "owner") {
    const usage = quotaUsage(body);
    return usage === null ? fallback : `Limite de sessions actives du compte atteinte : ${usage.used}/${usage.limit}. Une session doit avoir terminé son arrêt avant de libérer une place. Aucune nouvelle session n’a été créée.`;
  }

  if (body.code === "hot_session_quota_exceeded") {
    const usage = quotaUsage(body);
    if (usage === null) return fallback;
    const minimumToStop = Math.max(usage.used - usage.limit + 1, 1);

    if (body.scope === "owner") {
      return `Limite de sessions actives du compte atteinte : ${usage.used}/${usage.limit}. Arrêtez au moins ${minimumToStop} session${minimumToStop > 1 ? "s" : ""} active${minimumToStop > 1 ? "s" : ""} pour en démarrer une nouvelle. Ce quota de sessions actives est distinct de la limite de 100 sessions enregistrées par contexte.`;
    }
    if (body.scope === "context") {
      return `Limite de sessions actives du contexte atteinte : ${usage.used}/${usage.limit}. Arrêtez au moins ${minimumToStop} session${minimumToStop > 1 ? "s" : ""} active${minimumToStop > 1 ? "s" : ""} dans ce contexte pour en démarrer une nouvelle. Ce quota de sessions actives est distinct de la limite de 100 sessions enregistrées par contexte.`;
    }
    return fallback;
  }

  if (body.code === "frozen_quota_debt" && body.scope === "frozen_debt") {
    return "Impossible de créer une session : une autre session arrêtée du compte est en échec de sauvegarde. Contactez un opérateur avec l’identifiant de cette session pour diagnostiquer l’échec et reprendre la sauvegarde en conservant le profil. Réessayez après confirmation de la récupération. Ce blocage est distinct de la limite de 100 sessions enregistrées par contexte.";
  }

  if (body.code === "context_session_quota_exceeded") {
    return "Limite de 100 sessions enregistrées atteinte pour ce contexte. Supprimez au moins une session enregistrée de ce contexte avant d’en créer une nouvelle.";
  }

  return fallback;
}

/** Consume the response body once; invalid or non-JSON bodies use the stable fallback. */
export async function getCreateSessionErrorMessage(
  response: CreateSessionErrorResponse,
): Promise<string> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    body = undefined;
  }
  return formatCreateSessionError(response.status, body);
}

/** A stop refusal must not be mistaken for success or expose a raw child error. */
export async function getStopSessionErrorMessage(response: CreateSessionErrorResponse): Promise<string> {
  let body: unknown;
  try { body = await response.json(); } catch { body = undefined; }
  if (response.status === 202) {
    return "Arrêt pris en charge, résultat encore en attente. Consultez l’arrêt de cette session ; aucun nouvel arrêt ni aucune suppression ne sont lancés.";
  }
  if (response.status === 423 && isUnknownRecord(body) && body.destroy_allowed === false) {
    if (body.code === "checkpoint_in_progress") {
      return "Arrêt bloqué : une sauvegarde est en cours ou sa réservation attend une réconciliation après un échec. Consultez l’état de la session ; aucun nouvel arrêt ni aucune suppression ne sont lancés.";
    }
    if (body.code === "checkpoint_not_durable" || body.code === "checkpoint_status_missing") {
      return "Arrêt bloqué : la sauvegarde précédente n’est pas validée. Les données sont conservées ; une récupération doit être confirmée avant de poursuivre.";
    }
  }
  if (response.status === 504) {
    return "Le délai de réponse de l’arrêt est dépassé. L’opération peut encore être en cours : consultez l’état de la session. Aucun nouvel arrêt ni aucune suppression ne sont lancés.";
  }
  if (isUnknownRecord(body) && body.code === "frozen_snapshot_quota_exceeded") {
    return "Arrêt refusé : capacité de sauvegarde insuffisante. La session reste active et les sauvegardes existantes sont conservées. Contactez un opérateur pour vérifier la capacité ; aucune récupération automatique n’est lancée.";
  }
  if (isUnknownRecord(body) && body.code === "snapshot_capacity_exceeded") {
    return "Arrêt refusé : espace disque de sauvegarde insuffisant. La session reste active ; aucune sauvegarde existante n’est supprimée.";
  }
  return `Arrêt non confirmé (HTTP ${response.status}). Vérifiez l’état de la session avant de réessayer.`;
}

// ─── Component ────────────────────────────────────────────────────────

interface SessionListProps {
  defaultPickerOpen?: boolean;
  contextId?: string;
  contextName?: string;
  contextSummary?: BrowserContextSummary;
}

interface ProfileConflictDialogState extends ProfileRevisionConflict {
  sessionId: string;
  sessionName: string;
}

export default function SessionList({
  defaultPickerOpen = false,
  contextId,
  contextName,
  contextSummary,
}: SessionListProps = {}) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [contextNames, setContextNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [retiring, setRetiring] = useState(false);
  const [creating, setCreating] = useState(false);
  const contextVaultUnavailable = contextSummary !== undefined && contextSummary.vault_status !== "ready";
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showPicker, setShowPicker] = useState(contextId !== undefined && defaultPickerOpen);
  const [thawingIds, setThawingIds] = useState<Record<string, boolean>>({});
  const [observingStartIds, setObservingStartIds] = useState<Record<string, boolean>>({});
  const [observingStopIds, setObservingStopIds] = useState<Record<string, boolean>>({});
  const startingRequests = useRef(new Set<string>());
  const [rebootstrappingIds, setRebootstrappingIds] = useState<Record<string, boolean>>({});
  const [observingRebootstrapIds, setObservingRebootstrapIds] = useState<Record<string, boolean>>({});
  const [confirmRebootstrapId, setConfirmRebootstrapId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [savingProfileIds, setSavingProfileIds] = useState<Record<string, boolean>>({});
  const [profileConflict, setProfileConflict] = useState<ProfileConflictDialogState | null>(null);
  const [resolvingProfileConflict, setResolvingProfileConflict] = useState(false);
  const [copyContextName, setCopyContextName] = useState("");
  const [profileNotice, setProfileNotice] = useState<string | null>(null);
  const profileCommitKeysRef = useRef<Record<string, string>>({});

  // ── Selection mode (Drive-style with drag-to-select) ──────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);

  // Mutable drag state (no re-renders)
  const dragRef = useRef<{
    active: boolean;
    startIdx: number;
    prevIdx: number;
    baseSelection: Set<string>;
    mode: "select" | "deselect";
    didDrag: boolean;
  }>({ active: false, startIdx: -1, prevIdx: -1, baseSelection: new Set(), mode: "select", didDrag: false });

  // Cursor position + rAF id
  const autoScrollRef = useRef<{ raf: number; lastX: number; lastY: number }>({ raf: 0, lastX: 0, lastY: 0 });
  const tickRef = useRef<(() => void) | null>(null);

  // Available app types from current sessions
  const availableApps = useMemo(() => {
    const apps = new Set<string>();
    for (const s of sessions) if (s.browser) apps.add(s.browser);
    return Array.from(apps).sort();
  }, [sessions]);

  // Filtered sessions
  const filteredSessions = useMemo(() => {
    if (activeFilters.size === 0) return sessions;
    return sessions.filter((s) => activeFilters.has(s.browser ?? ""));
  }, [sessions, activeFilters]);

  const sessionGroups = useMemo(() => {
    if (contextId !== undefined) {
      return [
        {
          key: "scoped",
          title: null,
          description: null,
          sessions: filteredSessions,
        },
      ];
    }
    return [
      {
        key: "attached",
        title: "Sessions rattachées",
        description: "Sessions créées depuis un contexte navigateur persistant.",
        sessions: filteredSessions.filter(
          (session) => session.context_mode === "persistent",
        ),
      },
      {
        key: "legacy",
        title: "Legacy — sans contexte",
        description: "Sessions historiques qui ne sont rattachées à aucun contexte.",
        sessions: filteredSessions.filter(
          (session) => session.context_mode === "legacy",
        ),
      },
    ];
  }, [contextId, filteredSessions]);

  // Anti-stale-closure ref mirrors (always fresh in rAF / event listeners)
  const filteredSessionsRef = useRef(filteredSessions);
  filteredSessionsRef.current = filteredSessions;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  // Index lookup: session_id → index in filteredSessions
  const idToIndex = useMemo(() => {
    const map = new Map<string, number>();
    filteredSessions.forEach((s, i) => map.set(s.session_id, i));
    return map;
  }, [filteredSessions]);

  // Toggle a filter chip
  const toggleFilter = useCallback((app: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(app)) next.delete(app);
      else next.add(app);
      return next;
    });
  }, []);

  // Toggle selection mode
  const toggleSelectMode = useCallback(() => {
    setSelectMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }, []);

  // States that are transient — these sessions cannot be selected
  const isTransientState = useCallback((state: string) => {
    return state === "starting" || state === "stopping" || state === "connecting" || state === "destroying";
  }, []);

  // DOM lookup: find card index under point (X+Y for grid layout)
  const getCardIdxFromPoint = useCallback((x: number, y: number): number => {
    const grid = gridRef.current;
    if (!grid) return -1;
    const cards = grid.querySelectorAll<HTMLElement>("[data-session-idx]");
    for (let i = 0; i < cards.length; i++) {
      const card = cards.item(i);
      if (!card) continue;
      const rect = card.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return i;
    }
    // Clamp: above first → 0, below last → last
    const first = cards.item(0);
    const last = cards.item(cards.length - 1);
    if (first && last) {
      if (y < first.getBoundingClientRect().top) return 0;
      if (y > last.getBoundingClientRect().bottom) return cards.length - 1;
    }
    return -1;
  }, []);

  // Apply selection for range [startIdx..endIdx] with select/deselect mode
  const applySelection = useCallback((startIdx: number, endIdx: number) => {
    const d = dragRef.current;
    const fs = filteredSessionsRef.current;
    const lo = Math.min(startIdx, endIdx);
    const hi = Math.max(startIdx, endIdx);
    const next = new Set(d.baseSelection);

    if (d.mode === "select") {
      // Add range, remove outside-range items not in base
      for (let i = lo; i <= hi; i++) {
        const s = fs[i];
        if (s && !isTransientState(s.state)) next.add(s.session_id);
      }
      for (let i = 0; i < fs.length; i++) {
        const s = fs[i];
        if (s && (i < lo || i > hi) && !d.baseSelection.has(s.session_id)) {
          next.delete(s.session_id);
        }
      }
    } else {
      // Remove range, re-add outside-range items from base
      for (let i = lo; i <= hi; i++) {
        const s = fs[i];
        if (s) next.delete(s.session_id);
      }
      for (let i = 0; i < fs.length; i++) {
        const s = fs[i];
        if (s && (i < lo || i > hi) && d.baseSelection.has(s.session_id)) {
          next.add(s.session_id);
        }
      }
    }

    setSelectedIds(next);
  }, [isTransientState]);

  // Drag-select: mousedown starts range with select/deselect mode detection
  const handleCardMouseDown = useCallback(
    (id: string, state: string, e: React.MouseEvent) => {
      if (!selectMode || e.button !== 0) return;
      if (isTransientState(state)) return;
      e.preventDefault();

      const idx = idToIndex.get(id) ?? -1;
      const wasSelected = selectedIdsRef.current.has(id);

      dragRef.current = {
        active: true,
        startIdx: idx,
        prevIdx: idx,
        baseSelection: new Set(selectedIdsRef.current), // Always preserve
        mode: wasSelected ? "deselect" : "select",
        didDrag: false,
      };

      // Toggle the clicked item immediately
      applySelection(idx, idx);

      // Start rAF loop for auto-scroll + selection update
      autoScrollRef.current.lastX = e.clientX;
      autoScrollRef.current.lastY = e.clientY;
      if (!autoScrollRef.current.raf && tickRef.current) {
        autoScrollRef.current.raf = requestAnimationFrame(() => tickRef.current!());
      }
    },
    [selectMode, idToIndex, isTransientState, applySelection],
  );

  // Auto-scroll + document-level mousemove/mouseup (Drive-style)
  useEffect(() => {
    if (!selectMode) return;

    const EDGE_ZONE = 40;
    const MAX_SPEED = 14;

    // rAF tick: scroll + update selection via DOM lookup
    tickRef.current = () => {
      const d = dragRef.current;
      const as = autoScrollRef.current;
      if (!d.active) { as.raf = 0; return; }

      const vh = window.innerHeight;
      let speed = 0;

      // Linear acceleration based on distance to edge
      if (as.lastY >= vh) speed = MAX_SPEED;
      else if (as.lastY <= 0) speed = -MAX_SPEED;
      else if (as.lastY > vh - EDGE_ZONE) {
        speed = Math.ceil(MAX_SPEED * (1 - (vh - as.lastY) / EDGE_ZONE));
      } else if (as.lastY < EDGE_ZONE) {
        speed = -Math.ceil(MAX_SPEED * (1 - as.lastY / EDGE_ZONE));
      }

      if (speed !== 0) window.scrollBy(0, speed);

      // Update selection when scroll reveals new cards
      const idx = getCardIdxFromPoint(as.lastX, as.lastY);
      if (idx !== -1 && idx !== d.prevIdx) {
        d.prevIdx = idx;
        applySelection(d.startIdx, idx);
      }

      as.raf = requestAnimationFrame(() => tickRef.current!());
    };

    const handleMouseMove = (e: MouseEvent) => {
      autoScrollRef.current.lastX = e.clientX;
      autoScrollRef.current.lastY = e.clientY;

      const d = dragRef.current;
      if (!d.active) return;
      d.didDrag = true;

      // Update selection from mouse position via DOM lookup
      const idx = getCardIdxFromPoint(e.clientX, e.clientY);
      if (idx !== -1 && idx !== d.prevIdx) {
        d.prevIdx = idx;
        applySelection(d.startIdx, idx);
      }

      // Ensure rAF loop is running (for auto-scroll)
      if (!autoScrollRef.current.raf) {
        autoScrollRef.current.raf = requestAnimationFrame(() => tickRef.current!());
      }
    };

    const handleMouseUp = () => {
      dragRef.current.active = false;
      setTimeout(() => { dragRef.current.didDrag = false; }, 0);
      if (autoScrollRef.current.raf) {
        cancelAnimationFrame(autoScrollRef.current.raf);
        autoScrollRef.current.raf = 0;
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (autoScrollRef.current.raf) {
        cancelAnimationFrame(autoScrollRef.current.raf);
      }
    };
  }, [selectMode, getCardIdxFromPoint, applySelection]);

  // Clean up stale selectedIds when sessions change
  useEffect(() => {
    const validIds = new Set(sessions.map((s) => s.session_id));
    setSelectedIds((prev) => {
      const cleaned = new Set([...prev].filter((id) => validIds.has(id)));
      if (cleaned.size !== prev.size) return cleaned;
      return prev;
    });
  }, [sessions]);

  // Select all visible / deselect all
  const selectAll = useCallback(() => {
    setSelectedIds(new Set(filteredSessions.map((s) => s.session_id)));
  }, [filteredSessions]);
  const deselectAll = useCallback(() => setSelectedIds(new Set()), []);

  // ── Fetch sessions ────────────────────────────────────────────────

  const fetchSessions = useCallback(async () => {
    try {
      if (contextId !== undefined) {
        setSessions(await listContextSessions(contextId));
        setError(null);
        return;
      }
      const res = await apiFetch("/sessions");
      if (!res.ok) {
        setError(`Failed to load sessions: ${res.status}`);
        return;
      }
      const data: unknown = await res.json();
      if (!isSessionsListResponse(data)) {
        setError("Invalid response format");
        return;
      }
      const contexts = await listContexts();
      setContextNames(
        Object.fromEntries(contexts.map((context) => [context.id, context.name])),
      );
      setSessions(data.sessions);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error");
    } finally {
      setLoading(false);
    }
  }, [contextId]);

  // ── Polling ───────────────────────────────────────────────────────

  useEffect(() => {
    fetchSessions();
    pollRef.current = setInterval(fetchSessions, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
      }
    };
  }, [fetchSessions]);

  // ── Batch actions (need fetchSessions) ────────────────────────────

  const handleBatchStop = useCallback(async () => {
    const stoppable = filteredSessions.filter(
      (s) => selectedIds.has(s.session_id) && (s.state === "on" || s.state === "ongoing"),
    );
    if (stoppable.length === 0) return;
    if (!window.confirm(`Arrêter ${stoppable.length} session(s) ?`)) return;
    setActionError(null);
    const results = await Promise.all(stoppable.map(async (s) => {
      try {
        const response = await stopSession(s.session_id);
        return { id: s.session_id, error: response.status === 200 ? null : await getStopSessionErrorMessage(response) };
      } catch {
        return { id: s.session_id, error: "Arrêt non confirmé : erreur réseau. Vérifiez l’état de la session avant de réessayer." };
      }
    }));
    const failed = results.filter((result) => result.error !== null);
    setSelectedIds(new Set(failed.map((result) => result.id)));
    setActionError(failed.length ? failed.map((result) => result.error).filter((value, index, all) => all.indexOf(value) === index).join(" ") : null);
    await fetchSessions();
  }, [selectedIds, filteredSessions, fetchSessions]);

  // Suppression définitive — fonctionne quel que soit l'état : une session
  // active est d'abord arrêtée (jusqu'à `off`), puis détruite.
  const handleBatchDelete = useCallback(async () => {
    const selected = filteredSessions.filter((s) => selectedIds.has(s.session_id));
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selected.length} session(s) ?`)) return;
    // Keep cards and selection until the server actually confirms deletion.
    setActionError(null);
    const deleteOne = async (s: SessionSummary) => {
      try {
        const isActive =
          s.state === "on" || s.state === "ongoing" || s.state === "connecting" || s.state === "starting";
        if (isActive) {
          const stop = await stopSession(s.session_id);
          if (stop.status !== 200) {
            setActionError(await getStopSessionErrorMessage(stop));
            return; // An explicit stop refusal is never followed by DELETE.
          }
          // Attendre que la VM atteigne un état détruisible (`off`).
          let stopped = false;
          for (let i = 0; i < 25; i += 1) {
            const res = await apiFetch(`/sessions/${s.session_id}`);
            if (!res.ok) return; // déjà disparue
            const cur = (await res.json()) as { state?: string };
            if (cur.state === "off") { stopped = true; break; }
            await new Promise((r) => setTimeout(r, 1000));
          }
          if (!stopped) {
            setActionError("Arrêt non confirmé : suppression annulée, données conservées.");
            return;
          }
        }
        const response = await apiFetch(`/sessions/${s.session_id}`, { method: "DELETE" });
        if (!response.ok) {
          setActionError(response.status === 423
            ? "Suppression refusée : sauvegarde non durable. Utilisez l’archive récupérable pour conserver profils et coffre ; aucune capacité ne sera libérée."
            : `Suppression non confirmée (HTTP ${response.status}). La session est conservée.`);
          return;
        }
        setSessions(current => current.filter(item => item.session_id !== s.session_id));
        setSelectedIds(current => { const next = new Set(current); next.delete(s.session_id); return next; });
      } catch {
        setActionError("Opération non confirmée : vérifiez l’état de la session. Aucun nouvel essai automatique.");
      }
    };
    await Promise.all(selected.map(deleteOne));
    fetchSessions();
  }, [selectedIds, filteredSessions, fetchSessions]);

  // ── Create session ────────────────────────────────────────────────

  const handleBatchArchive = async () => {
    const selected = filteredSessions.filter(session => selectedIds.has(session.session_id));
    if (retiring || selected.length === 0) return;
    if (selected.some(session => session.state !== "off")) {
      setActionError("Arrêtez les sessions et laissez leurs opérations se terminer avant l’archivage.");
      return;
    }
    if (!window.confirm(`Archiver ${selected.length} session(s) arrêtée(s) ? Profils, coffre et sauvegardes sont conservés. Restauration possible dans les archives, sans démarrage automatique.`)) return;
    setRetiring(true);
    setActionError(null);
    try {
      for (const session of selected) {
        await setRetirement("session", session.session_id, true);
        setSelectedIds(current => { const next = new Set(current); next.delete(session.session_id); return next; });
      }
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "Archive non confirmée. Actualisez avant de réessayer.");
    } finally { setRetiring(false); await fetchSessions(); }
  };

  const handleCreate = useCallback(
    async (appName: string) => {
      // A previous stop/start lookup must not mask this creation's result.
      setActionError(null);
      setProfileNotice(null);
      setError(null);
      if (contextId === undefined) {
        setError("Choisissez un contexte avant de créer une session.");
        return;
      }
      if (contextSummary !== undefined && contextSummary.vault_status !== "ready") {
        setError("Le coffre Firefox doit être prêt avant la création d’une session.");
        return;
      }
      setCreating(true);
      try {
        const res = await createSessionInContext(contextId, appName);
        if (!res.ok) {
          setError(await getCreateSessionErrorMessage(res));
          return;
        }
        const data: unknown = await res.json();
        if (!isCreateSessionResponse(data)) {
          setError("Invalid create response format");
          return;
        }
        // Token stored in sessionStorage for immediate use in SessionView
        sessionStorage.setItem(
          `session_token_${data.session_id}`,
          data.session_token,
        );
        navigate(`/sessions/${data.session_id}`);
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "Failed to create session",
        );
      } finally {
        setCreating(false);
      }
    },
    [contextId, contextSummary, navigate],
  );

  const handleSaveProfile = useCallback(
    async (session: SessionSummary, event: React.MouseEvent) => {
      event.stopPropagation();
      if (
        contextId === undefined ||
        session.context_id !== contextId ||
        !session.profile_actions_allowed
      ) {
        setError("La session ne correspond pas au contexte affiché.");
        return;
      }
      setSavingProfileIds((current) => ({ ...current, [session.session_id]: true }));
      setError(null);
      setProfileNotice(null);
      const idempotencyKey =
        profileCommitKeysRef.current[session.session_id] ?? crypto.randomUUID();
      profileCommitKeysRef.current[session.session_id] = idempotencyKey;
      try {
        const result = await commitSessionProfile(
          session.session_id,
          contextId,
          idempotencyKey,
        );
        delete profileCommitKeysRef.current[session.session_id];
        if (result.kind === "conflict") {
          setCopyContextName("");
          setProfileConflict({
            ...result.conflict,
            sessionId: session.session_id,
            sessionName: session.name || `Session ${truncateId(session.session_id)}`,
          });
          return;
        }
        setProfileConflict(null);
        setProfileNotice(
          `Profil de ${session.name || `Session ${truncateId(session.session_id)}`} sauvegardé en révision #${result.value.revision}.`,
        );
        await fetchSessions();
      } catch (cause: unknown) {
        const message = cause instanceof Error ? cause.message : "";
        setError(
          message.startsWith("Sauvegarde du profil")
            ? message
            : "Sauvegarde du profil impossible (erreur réseau).",
        );
      } finally {
        setSavingProfileIds((current) => ({ ...current, [session.session_id]: false }));
      }
    },
    [contextId, fetchSessions],
  );

  const handleResolveProfileConflict = useCallback(
    async (resolution: ProfileCandidateResolution) => {
      if (contextId === undefined || profileConflict === null) return;
      if (resolution.action === "copy_to_new_context" && resolution.name.trim().length === 0) {
        setError("Le nom du nouveau contexte est requis.");
        return;
      }
      setResolvingProfileConflict(true);
      setError(null);
      try {
        const normalizedResolution =
          resolution.action === "copy_to_new_context"
            ? { ...resolution, name: resolution.name.trim() }
            : resolution;
        const result = await resolveProfileCandidate(
          contextId,
          profileConflict.candidate_id,
          normalizedResolution,
        );
        if (result.action === "keep_canonical") {
          setProfileNotice("Conflit résolu : le profil actuel du contexte a été conservé.");
        } else if (result.action === "promote_candidate") {
          setProfileNotice(
            `Conflit résolu : les changements de la session ont été publiés en révision #${result.revision}.`,
          );
        } else {
          setProfileNotice(
            `Conflit résolu : les changements ont été copiés dans le nouveau contexte « ${normalizedResolution.action === "copy_to_new_context" ? normalizedResolution.name : ""} » en révision #${result.revision}.`,
          );
        }
        setProfileConflict(null);
        setCopyContextName("");
        await fetchSessions();
      } catch (cause: unknown) {
        const message = cause instanceof Error ? cause.message : "";
        setError(
          message.startsWith("Résolution du conflit")
            ? message
            : "Résolution du conflit impossible (erreur réseau).",
        );
      } finally {
        setResolvingProfileConflict(false);
      }
    },
    [contextId, fetchSessions, profileConflict],
  );

  // Explicit two-click lifecycle action, not a vault credential form. The
  // server owns checkpoint/archive/bootstrap; no secret is returned to this UI.
  // After a lost response, only an explicit read-only observation is offered.
  const handleObserveVaultRebootstrap = useCallback(
    async (session: SessionSummary, event: React.MouseEvent) => {
      event.stopPropagation();
      if (contextId === undefined || session.context_id !== contextId ||
          !["firefox", "chrome"].includes(session.browser ?? "") || !session.profile_actions_allowed ||
          observingRebootstrapIds[session.session_id]) return;
      setObservingRebootstrapIds((current) => ({ ...current, [session.session_id]: true }));
      setActionError(null);
      setProfileNotice(null);
      const controller = new AbortController();
      // Cancels ONLY this read-only GET, never the server-owned lifecycle.
      const timeout = window.setTimeout(() => controller.abort(), 10_000);
      try {
        const response = await apiFetch(`/sessions/${session.session_id}/vault/rebootstrap`, {
          method: "GET", signal: controller.signal,
        });
        const result: unknown = response.ok ? await response.json() : undefined;
        if (!isUnknownRecord(result) || result.session_id !== session.session_id ||
            typeof result.operation_id !== "string" || typeof result.running !== "boolean" ||
            typeof result.overdue !== "boolean" || typeof result.retryable !== "boolean") {
          setActionError("Observation de reprise indisponible. Cela ne prouve pas que l’opération n’a pas eu lieu ; aucun nouvel essai automatique.");
          return;
        }
        if (result.running) {
          setRebootstrappingIds((current) => ({ ...current, [session.session_id]: true }));
          setProfileNotice(result.overdue
            ? "Reprise toujours détenue par le serveur après 10 minutes. Aucun nouvel essai : faites inspecter cette opération par un opérateur."
            : "Reprise poursuivie côté serveur sur la même session. Consultez son état sans relancer l’opération.");
        } else if (result.phase === "complete" && result.state === "on" && result.retention === "hot") {
          setRebootstrappingIds((current) => ({ ...current, [session.session_id]: false }));
          setProfileNotice("Firefox redémarré sur la même session. Reconnectez-vous pour vérifier le coffre ; aucune nouvelle session créée.");
        } else if (result.phase === "failed") {
          setRebootstrappingIds((current) => ({ ...current, [session.session_id]: !result.retryable }));
          setActionError(result.retryable
            ? "Reprise terminée en échec ; checkpoint exact conservé et reprise possible après vérification opérateur. Aucun nouvel essai automatique."
            : "Reprise terminée en échec ; aucune reprise sûre confirmée. Faites inspecter le checkpoint, sans répéter l’opération.");
        } else {
          setActionError("État de reprise inattendu ; aucun nouvel essai automatique.");
        }
      } catch {
        setActionError("Observation de reprise indisponible (erreur réseau ou délai de 10 secondes). L’opération serveur n’est pas annulée ; aucun nouvel essai automatique.");
      } finally {
        window.clearTimeout(timeout);
        setObservingRebootstrapIds((current) => ({ ...current, [session.session_id]: false }));
        await fetchSessions();
      }
    },
    [contextId, observingRebootstrapIds, fetchSessions],
  );

  const handleRebootstrapVault = useCallback(
    async (session: SessionSummary, event: React.MouseEvent) => {
      event.stopPropagation();
      if (contextId === undefined || session.context_id !== contextId ||
          !["firefox", "chrome"].includes(session.browser ?? "") || !session.profile_actions_allowed ||
          rebootstrappingIds[session.session_id]) return;
      if (confirmRebootstrapId !== session.session_id) {
        setConfirmRebootstrapId(session.session_id);
        return;
      }
      setConfirmRebootstrapId(null);
      setRebootstrappingIds((current) => ({ ...current, [session.session_id]: true }));
      setActionError(null);
      setProfileNotice(null);
      let unresolved = true;
      try {
        const response = await apiFetch(`/sessions/${session.session_id}/vault/rebootstrap`, {
          method: "POST",
        });
        if (response.status === 202) {
          setProfileNotice("Reprise poursuivie côté serveur sur la même session. Consultez son état sans relancer l’opération.");
        } else if (response.ok) {
          const result: unknown = await response.json();
          if (!isUnknownRecord(result) || result.session_id !== session.session_id ||
              result.state !== "on" || result.retention !== "hot") {
            setActionError("Réponse de reprise inattendue. Vérifiez l’état de la session ; aucun nouvel essai automatique.");
          } else {
            unresolved = false;
            setProfileNotice("Firefox redémarré sur la même session. Reconnectez-vous pour vérifier le coffre ; aucune nouvelle session créée.");
          }
        } else {
          setActionError(`Reprise du coffre non confirmée (HTTP ${response.status}). Vérifiez la capacité et le checkpoint ; les données sont conservées, aucun nouvel essai automatique.`);
        }
      } catch {
        setActionError("Reprise du coffre non confirmée : erreur réseau. Consultez l’état de reprise sans répéter l’opération ; le serveur poursuit une opération déjà acceptée.");
      } finally {
        setRebootstrappingIds((current) => ({ ...current, [session.session_id]: unresolved }));
        await fetchSessions();
      }
    },
    [contextId, confirmRebootstrapId, rebootstrappingIds, fetchSessions],
  );

  // ── Start (restart) stopped session — disable during start, navigate after ─

  const handleStart = useCallback(
    async (sessionId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (startingRequests.current.has(sessionId)) return;
      startingRequests.current.add(sessionId);
      setThawingIds((prev) => ({ ...prev, [sessionId]: true }));
      setActionError(null);
      setProfileNotice(null);
      let unresolved = true;
      try {
        const res = await startSession(sessionId);
        if (res.status === 202) {
          setProfileNotice("Démarrage poursuivi côté serveur. Consultez son résultat sans répéter la commande.");
        } else if (res.status === 200) {
          // The existing 200 contract means durable completion of this UUID;
          // 202 is explicitly not completion and never navigates here.
          unresolved = false;
          navigate(`/sessions/${sessionId}`);
        } else {
          let failure: unknown;
          try { failure = await res.json(); } catch { failure = undefined; }
          if (res.status === 429 && isUnknownRecord(failure) &&
              (failure.code === "active_session_quota_exceeded" || failure.code === "hot_session_quota_exceeded") &&
              quotaUsage(failure) !== null && (failure.scope === "owner" || failure.scope === "context")) {
            // These typed admission errors precede any start helper. An unknown
            // 429 remains unresolved; do not infer safety from HTTP alone.
            unresolved = false;
            const usage = quotaUsage(failure)!;
            const scope = failure.scope === "context" ? "du contexte" : "du compte";
            setActionError(`Démarrage refusé : limite de sessions actives ${scope} atteinte (${usage.used}/${usage.limit}). Aucun démarrage n’a commencé. Une session doit avoir terminé son arrêt avant de libérer une place.`);
          } else if (res.status === 503 && isUnknownRecord(failure) &&
              failure.code === "session_start_capacity_reached" && failure.destroy_allowed === false) {
            // This exact admission refusal guarantees no operation was started.
            // Release the local lock for an explicit user retry, never replay it.
            unresolved = false;
            setActionError("Capacité de démarrage temporairement atteinte : aucune opération n’a commencé. Vous pouvez réessayer le démarrage plus tard.");
          } else {
            // Failed, missing or malformed outcomes can follow real side effects.
            // Keep the lock and the existing read-only result action.
            setActionError(`Démarrage non confirmé (HTTP ${res.status}). Consultez son résultat avant toute autre action.`);
          }
        }
      } catch {
        setActionError("Réponse de démarrage perdue. Le serveur conserve le travail accepté ; consultez son résultat sans répéter la commande.");
      } finally {
        if (!unresolved) startingRequests.current.delete(sessionId);
        setThawingIds((prev) => ({ ...prev, [sessionId]: unresolved }));
        await fetchSessions();
      }
    },
    [fetchSessions, navigate],
  );

  const handleObserveStart = useCallback(
    async (sessionId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      if (observingStartIds[sessionId]) return;
      setObservingStartIds((current) => ({ ...current, [sessionId]: true }));
      setActionError(null);
      setProfileNotice(null);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10_000);
      try {
        const response = await apiFetch(`/sessions/${sessionId}/start`, {
          method: "GET", signal: controller.signal,
        });
        const result: unknown = response.ok ? await response.json() : undefined;
        if (!isUnknownRecord(result) || result.session_id !== sessionId || result.kind !== "start" ||
            typeof result.operation_id !== "string" || typeof result.running !== "boolean" ||
            typeof result.overdue !== "boolean") {
          setActionError("Résultat de démarrage indisponible. Cela ne prouve pas l’absence d’effet ; faites rapprocher l’état durable et les ressources, sans relancer.");
          return;
        }
        if (result.running) {
          startingRequests.current.add(sessionId);
          setThawingIds((current) => ({ ...current, [sessionId]: true }));
          setProfileNotice(result.overdue
            ? "Démarrage toujours détenu par le serveur après 10 minutes. Inspection opérateur requise ; aucun helper n’est annulé par cette lecture."
            : "Démarrage en cours côté serveur sur la même session. Aucun nouvel appel de démarrage.");
        } else if (result.phase === "failed" && result.running === false && result.state === "off" &&
            (result.code === "active_quota_reached" || result.code === "hot_quota_reached")) {
          // These retained codes originate exclusively in admission before a
          // helper starts. The historical generic retryable=false is not a
          // claim that physical resources were created by a quota refusal.
          startingRequests.current.delete(sessionId);
          setThawingIds((current) => ({ ...current, [sessionId]: false }));
          setActionError("Démarrage refusé : limite de sessions actives atteinte. Aucun démarrage n’a commencé ; la session reste arrêtée. Une session doit avoir terminé son arrêt avant de libérer une place.");
        } else if (result.phase === "complete" &&
            (result.state === "on" || result.state === "connecting" || result.state === "ongoing") &&
            result.retention === "hot") {
          startingRequests.current.delete(sessionId);
          setThawingIds((current) => ({ ...current, [sessionId]: false }));
          setProfileNotice("Démarrage confirmé sur la même session. Connectez-vous pour vérifier le navigateur et le coffre.");
        } else {
          startingRequests.current.add(sessionId);
          setThawingIds((current) => ({ ...current, [sessionId]: true }));
          setActionError("Démarrage non confirmé. Faites inspecter son issue durable et les ressources physiques avant tout nouvel essai.");
        }
      } catch {
        setActionError("Consultation du démarrage indisponible (réseau ou délai de 10 secondes). Aucun nouvel appel de démarrage.");
      } finally {
        window.clearTimeout(timeout);
        setObservingStartIds((current) => ({ ...current, [sessionId]: false }));
        await fetchSessions();
      }
    },
    [observingStartIds, fetchSessions],
  );

  const handleObserveStop = useCallback(
    async (sessionId: string, event: React.MouseEvent) => {
      event.stopPropagation();
      if (observingStopIds[sessionId]) return;
      setObservingStopIds((current) => ({ ...current, [sessionId]: true }));
      setActionError(null);
      setProfileNotice(null);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 10_000);
      try {
        const response = await apiFetch(`/sessions/${sessionId}/stop`, {
          method: "GET", signal: controller.signal,
        });
        const result: unknown = response.ok ? await response.json() : undefined;
        if (!isUnknownRecord(result) || result.session_id !== sessionId || result.kind !== "stop" ||
            typeof result.operation_id !== "string" || typeof result.running !== "boolean") {
          setActionError("Résultat d’arrêt indisponible. Les données sont conservées ; une inspection de la session est nécessaire avant toute nouvelle commande.");
        } else if (result.running) {
          setProfileNotice("Arrêt et sauvegarde en cours côté serveur. Cette consultation ne relance aucune opération.");
        } else if (result.phase === "complete" && result.state === "off") {
          setProfileNotice("Arrêt terminé pour cette session.");
        } else {
          setActionError("L’arrêt n’a pas abouti. Les données et les réservations de sauvegarde sont conservées ; une récupération doit être vérifiée avant de poursuivre.");
        }
      } catch {
        setActionError("Consultation de l’arrêt indisponible. Aucun nouvel arrêt ni aucune suppression ne sont lancés.");
      } finally {
        window.clearTimeout(timeout);
        setObservingStopIds((current) => ({ ...current, [sessionId]: false }));
        await fetchSessions();
      }
    },
    [observingStopIds, fetchSessions],
  );

  // ── Rename session (F2: click-to-edit) ────────────────────────────

  const handleStartRename = useCallback(
    (sessionId: string, currentName: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingId(sessionId);
      setEditName(currentName);
    },
    [],
  );

  const handleSaveRename = useCallback(
    async (sessionId: string) => {
      const trimmed = editName.trim();
      if (trimmed) {
        await renameSession(sessionId, trimmed).catch(() => {});
        fetchSessions();
      }
      setEditingId(null);
    },
    [editName, fetchSessions],
  );

  const handleCancelRename = useCallback(() => {
    setEditingId(null);
  }, []);

  // ── Card action: connect/resume/thaw ──────────────────────────────

  const handleCardAction = useCallback(
    (session: SessionSummary, e: React.MouseEvent) => {
      e.stopPropagation();
      const ds = getDisplayState(session.state);
      if (ds === "off") {
        handleStart(session.session_id, e);
      } else {
        navigate(`/sessions/${session.session_id}`);
      }
    },
    [handleStart, navigate],
  );

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: "0 auto",
        padding: 24,
        fontFamily: "system-ui, sans-serif",
        color: "#e5e7eb",
      }}
    >
      {/* Scrollbar dark-theme for dropdown panel */}
      <style>{`
        [role="listbox"]::-webkit-scrollbar { width: 6px; }
        [role="listbox"]::-webkit-scrollbar-track { background: #1f2937; }
        [role="listbox"]::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 3px; }
        [role="listbox"]::-webkit-scrollbar-thumb:hover { background: #6b7280; }
        [role="listbox"] { scrollbar-width: thin; scrollbar-color: #4b5563 #1f2937; }
      `}</style>

      <RetirementsPanel onRestored={() => void fetchSessions()} />

      {/* Header row: title + actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
            {contextName === undefined
              ? "Toutes les sessions"
              : `Sessions — ${contextName}`}
          </h1>
          {contextId !== undefined && (
            <>
              <Link to="/contexts" style={{ color: "#93c5fd", fontSize: 12 }}>
                ← Retour aux contextes
              </Link>
              {contextSummary !== undefined && (
                <div
                  aria-label="Résumé du contexte"
                  style={{ color: "#9ca3af", fontSize: 12, marginTop: 6 }}
                >
                  {contextSummary.session_count} sessions enregistrées
                  {" · "}{contextSummary.active_session_count} actives
                  {" · "}révision #{contextSummary.current_revision}
                  {" · "}profil {formatContextStatus(contextSummary.status).toLowerCase()}
                </div>
              )}
              <p
                style={{ color: "#9ca3af", fontSize: 11, margin: "6px 0 0", maxWidth: 760 }}
              >
                Les sessions partagent la même révision de base. Leurs changements ne se synchronisent pas en direct :
                sauvegardez le profil avant de les réutiliser ailleurs.
              </p>
            </>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {contextId !== undefined ? (
            <button
              disabled={creating || contextVaultUnavailable}
              onClick={() => {
                if (!creating && !contextVaultUnavailable) setShowPicker(true);
              }}
              title={
                contextVaultUnavailable
                  ? "Le coffre doit être prêt avant la création d’une session"
                  : undefined
              }
              style={{
                background:
                  creating || contextVaultUnavailable ? "#374151" : "#2563eb",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 500,
                cursor:
                  creating || contextVaultUnavailable ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {creating
                ? "Creating..."
                : "+ New Session"}
            </button>
          ) : (
            <Link to="/contexts" style={{ color: "#93c5fd", fontSize: 13 }}>
              Choisir un contexte pour créer
            </Link>
          )}
          <button
            onClick={fetchSessions}
            aria-label="Refresh sessions list"
            style={{
              background: "#374151",
              color: "#e5e7eb",
              border: "none",
              borderRadius: 6,
              padding: "8px 12px",
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            ↻
          </button>
        </div>
      </div>

      {/* Selection toolbar (Drive-style) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
          minHeight: 34,
        }}
      >
        {/* Select toggle */}
        <button
          onClick={toggleSelectMode}
          style={{
            background: selectMode ? "#3b82f6" : "transparent",
            color: selectMode ? "#fff" : "#9ca3af",
            border: selectMode ? "1px solid #3b82f6" : "1px solid #4b5563",
            borderRadius: 6,
            padding: "5px 12px",
            fontSize: 12,
            fontWeight: 500,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          Sélectionner
        </button>

        {/* Separator */}
        {availableApps.length > 0 && (
          <div style={{ width: 1, height: 20, background: "#374151" }} />
        )}

        {/* App filter chips */}
        {availableApps.map((app) => {
          const active = activeFilters.has(app);
          const label = (APP_LABELS[app] ?? app).toUpperCase();
          const count = sessions.filter((s) => s.browser === app).length;
          return (
            <button
              key={app}
              onClick={() => toggleFilter(app)}
              style={{
                background: active ? "#7c3aed" : "transparent",
                color: active ? "#fff" : "#a78bfa",
                border: active ? "1px solid #7c3aed" : "1px solid #4b5563",
                borderRadius: 6,
                padding: "4px 10px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                letterSpacing: "0.03em",
              }}
            >
              {label}
              <span style={{ marginLeft: 4, opacity: 0.7, fontSize: 10 }}>
                {count}
              </span>
            </button>
          );
        })}

        {/* Sélection groupée — Tout/Aucun dès que le mode sélection est actif ;
            le compteur et les actions (Supprimer/Arrêter) quand ≥1 sélectionnée. */}
        {selectMode && (() => {
          const hasStoppable = filteredSessions.some((s) => selectedIds.has(s.session_id) && (s.state === "on" || s.state === "ongoing"));
          const ghostBtn: React.CSSProperties = {
            background: "transparent",
            color: "#9ca3af",
            border: "1px solid #4b5563",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 11,
            cursor: "pointer",
          };
          const actionBtn = (bg: string, fg: string): React.CSSProperties => ({
            background: bg,
            color: fg,
            border: "none",
            borderRadius: 6,
            padding: "4px 10px",
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
          });
          return (
          <>
            <div style={{ width: 1, height: 20, background: "#374151" }} />
            <button onClick={selectAll} style={ghostBtn}>Tout</button>
            <button onClick={deselectAll} style={ghostBtn}>Aucun</button>
            {selectedIds.size > 0 && (
              <>
                <span style={{ fontSize: 12, color: "#e5e7eb", fontWeight: 500 }}>
                  {selectedIds.size} sélectionnée{selectedIds.size > 1 ? "s" : ""}
                </span>
                {hasStoppable && (
                  <button
                    onClick={handleBatchStop}
                    title="Met la session en pause — tu pourras la reprendre via « Connect ». Le profil est sauvegardé."
                    style={actionBtn("#1e3a5f", "#7dd3fc")}
                  >
                    Arrêter
                  </button>
                )}
                <button
                  onClick={handleBatchDelete}
                  disabled={retiring}
                  title="Supprime définitivement la/les session(s) — l'active est d'abord arrêtée."
                  style={actionBtn("#991b1b", "#fca5a5")}
                >
                  Supprimer
                </button>
                <button onClick={() => void handleBatchArchive()} disabled={retiring} style={ghostBtn}>
                  Archiver (données conservées)
                </button>
              </>
            )}
          </>
          );
        })()}
      </div>

      {/* Error */}
      {(error !== null || actionError !== null) && (
        <div
          role="alert"
          style={{
            background: "#7f1d1d",
            color: "#fca5a5",
            padding: "8px 12px",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {actionError ?? error}
        </div>
      )}

      {profileNotice !== null && (
        <div
          role="status"
          data-testid="session-profile-notice"
          style={{
            background: "#064e3b",
            color: "#a7f3d0",
            padding: "8px 12px",
            borderRadius: 6,
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          {profileNotice}
        </div>
      )}

      {profileConflict !== null && (
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Résoudre le conflit de profil"
          style={{
            background: "#1f2937",
            border: "1px solid #f59e0b",
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            maxWidth: 760,
          }}
        >
          <h2 style={{ margin: "0 0 8px", fontSize: 17 }}>Conflit de profil</h2>
          <p style={{ margin: "0 0 12px", color: "#fcd34d", fontSize: 13 }}>
            {profileConflict.sessionName} partait de la révision #{profileConflict.base_revision},
            alors que le contexte est déjà en révision #{profileConflict.current_revision}. Aucun
            écrasement automatique n’est effectué.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            <button
              type="button"
              disabled={resolvingProfileConflict}
              onClick={() => void handleResolveProfileConflict({ action: "keep_canonical" })}
            >
              Conserver le profil actuel
            </button>
            <button
              type="button"
              disabled={resolvingProfileConflict}
              onClick={() => void handleResolveProfileConflict({ action: "promote_candidate" })}
            >
              Utiliser les changements de cette session
            </button>
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            Nom du nouveau contexte
            <input
              value={copyContextName}
              maxLength={100}
              disabled={resolvingProfileConflict}
              onChange={(event) => setCopyContextName(event.target.value)}
            />
            <button
              type="button"
              disabled={resolvingProfileConflict || copyContextName.trim().length === 0}
              onClick={() =>
                void handleResolveProfileConflict({
                  action: "copy_to_new_context",
                  name: copyContextName,
                })
              }
            >
              Copier dans un nouveau contexte
            </button>
          </label>
          <button
            type="button"
            disabled={resolvingProfileConflict}
            onClick={() => setProfileConflict(null)}
            style={{ marginTop: 12 }}
          >
            Fermer
          </button>
        </section>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
          Loading sessions...
        </div>
      )}

      {/* Empty state */}
      {!loading && sessions.length === 0 && error === null && (
        <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
          {contextId === undefined
            ? "Aucune session. Choisissez un contexte pour en créer une."
            : "Aucune session dans ce contexte. Cliquez sur « + New Session » pour démarrer."}
        </div>
      )}

      {/* Filtered empty */}
      {!loading && sessions.length > 0 && filteredSessions.length === 0 && (
        <div style={{ textAlign: "center", padding: 32, color: "#9ca3af" }}>
          No sessions match the active filters.
        </div>
      )}

      {/* Session groups and grids (F2) */}
      {filteredSessions.length > 0 && (
        <div ref={gridRef}>
          {sessionGroups.map((group) => (
            <section
              key={group.key}
              className="resource-section"
              aria-labelledby={
                group.title === null ? undefined : `session-group-${group.key}`
              }
              aria-label={group.title === null ? "Sessions du contexte" : undefined}
            >
              {group.title !== null && (
                <header className="resource-section-header">
                  <div>
                    <h2
                      id={`session-group-${group.key}`}
                      className="resource-section-title"
                    >
                      {group.title} ({group.sessions.length})
                    </h2>
                    <p className="resource-section-description">
                      {group.description}
                    </p>
                  </div>
                  <span className="resource-section-count" aria-hidden="true">
                    {group.sessions.length}
                  </span>
                </header>
              )}
              {group.sessions.length === 0 ? (
                <p style={{ color: "#6b7280", fontSize: 12 }}>
                  Aucune session dans cette section.
                </p>
              ) : (
                <div
                  role="list"
                  aria-label={
                    group.title === null ? "Sessions grid" : `${group.title} — liste`
                  }
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 12,
                    maxWidth: 1400,
                  }}
                >
          {group.sessions.map((session) => {
            const ds = getDisplayState(session.state);
            const dsConfig = DISPLAY_STATE_CONFIG[ds];
            const appId = session.browser ?? "";
            const appLabel = appId ? (APP_LABELS[appId] ?? appId) : "App";
            const isEditing = editingId === session.session_id;
            const displayName =
              session.name || `Session ${truncateId(session.session_id)}`;
            const isThawing = thawingIds[session.session_id] ?? false;
            const isTransitioning = ds === "transitioning";
            const thumb = session.thumbnail ?? null;

            // Action button label — always "Connect" (start is transparent)
            let actionLabel = isThawing ? "Starting..." : "Connect";
            if (isTransitioning && !isThawing) actionLabel = "...";

            const isSelected = selectedIds.has(session.session_id);
            const isTransient = isTransientState(session.state);
            const borderIdle =
              ds === "ongoing" ? "#22c55e40" : ds === "on" ? "#eab30840" : "#374151";
            const borderSelected = "#3b82f6";

            return (
              <div
                key={session.session_id}
                className="resource-card"
                role="listitem"
                data-session-idx={idToIndex.get(session.session_id)}
                onClick={() => {
                  // En mode sélection, la (dé)sélection est gérée entièrement par
                  // onMouseDown (+ glisser). Ne PAS re-toggler ici : ça faisait un
                  // double toggle sur un clic immobile → la sélection se vidait
                  // aussitôt (bug « tout disparaît »).
                  if (selectMode) return;
                  if (!isEditing && ds !== "off" && !rebootstrappingIds[session.session_id]) {
                    navigate(`/sessions/${session.session_id}`);
                  }
                }}
                onMouseDown={(e) => handleCardMouseDown(session.session_id, session.state, e)}
                style={{
                  background: isSelected ? "#1e3a5f" : "#1f2937",
                  border: `1px solid ${isSelected ? borderSelected : borderIdle}`,
                  borderRadius: 10,
                  padding: "12px 10px 10px",
                  cursor: selectMode ? "pointer" : isEditing ? "default" : "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  minHeight: 140,
                  position: "relative",
                  transition: "border-color 0.2s, background 0.2s, opacity 0.2s",
                  userSelect: selectMode ? "none" : "auto",
                  opacity: selectMode && isTransient ? 0.4 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isEditing && !selectMode)
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      dsConfig.color;
                }}
                onMouseLeave={(e) => {
                  if (!isEditing && !selectMode)
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      isSelected ? borderSelected : borderIdle;
                }}
              >
                {/* Selection checkbox */}
                {selectMode && (
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      zIndex: 2,
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: isSelected ? "none" : "2px solid #6b7280",
                      background: isSelected ? "#3b82f6" : "rgba(17,24,39,0.7)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 12,
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {isSelected ? "✓" : ""}
                  </div>
                )}

                {/* Thumbnail preview */}
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "16 / 9",
                    borderRadius: 6,
                    overflow: "hidden",
                    background: thumb ? "none" : "#111827",
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt=""
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        opacity: ds === "off" ? 0.4 : 0.85,
                        filter: ds === "off" ? "grayscale(0.6)" : "none",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#374151",
                        fontSize: 20,
                      }}
                    >
                      {appLabel.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {/* State dot overlay */}
                  <div
                    style={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: dsConfig.dotColor,
                      boxShadow: `0 0 6px ${dsConfig.dotColor}`,
                    }}
                    title={dsConfig.label}
                  />
                </div>

                {/* App + State row */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {appLabel}
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: dsConfig.color,
                      textTransform: "uppercase",
                      flexShrink: 0,
                    }}
                  >
                    {dsConfig.label}
                  </div>
                </div>

                {/* Session name (editable) */}
                {isEditing ? (
                  <input
                    autoFocus
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleSaveRename(session.session_id);
                      if (e.key === "Escape") handleCancelRename();
                    }}
                    onBlur={() => handleSaveRename(session.session_id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: "#111827",
                      color: "#e5e7eb",
                      border: "1px solid #4b5563",
                      borderRadius: 4,
                      padding: "2px 6px",
                      fontSize: 11,
                      textAlign: "center",
                      width: "100%",
                      outline: "none",
                    }}
                  />
                ) : (
                  <div
                    onClick={(e) =>
                      handleStartRename(session.session_id, displayName, e)
                    }
                    title="Click to rename"
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#c9cdd3",
                      textAlign: "center",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      width: "100%",
                      cursor: "text",
                    }}
                  >
                    {displayName}
                  </div>
                )}

                {contextId === undefined &&
                  session.context_mode === "persistent" &&
                  session.context_id !== undefined && (
                    <div
                      aria-label={`Contexte de ${displayName}`}
                      style={{
                        width: "100%",
                        borderRadius: 5,
                        background: "#172554",
                        color: "#bfdbfe",
                        padding: "4px 6px",
                        fontSize: 10,
                        textAlign: "center",
                      }}
                    >
                      Contexte : {contextNames[session.context_id] ?? "Contexte inconnu"}
                    </div>
                  )}

                {session.context_mode === "legacy" && (
                  <div
                    style={{
                      width: "100%",
                      borderRadius: 5,
                      border: "1px solid #92400e",
                      background: "#451a03",
                      padding: "5px 6px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      aria-label={`Type de contexte de ${displayName}`}
                      style={{ color: "#fcd34d", fontSize: 10, fontWeight: 700 }}
                    >
                      {session.context_label}
                    </div>
                    <div style={{ color: "#fed7aa", fontSize: 9, marginTop: 3 }}>
                      Son profil ne peut pas être sauvegardé dans un contexte.
                    </div>
                  </div>
                )}

                {(session.context_base_revision !== undefined ||
                  session.profile_sync_status !== undefined) && (
                  <div
                    aria-label={`Profil de ${displayName}`}
                    style={{
                      fontSize: 9,
                      color:
                        session.profile_sync_status === "error" ||
                        session.profile_sync_status === "failed"
                          ? "#f87171"
                          : "#9ca3af",
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    {session.context_base_revision !== undefined
                      ? `Base #${session.context_base_revision}`
                      : "Base indisponible"}
                    {session.profile_sync_status !== undefined
                      ? ` · profil ${session.profile_sync_status}`
                      : ""}
                  </div>
                )}

                {/* Last connected (relative time) */}
                <div
                  style={{
                    fontSize: 9,
                    color: "#6b7280",
                    textAlign: "center",
                  }}
                >
                  {session.last_connected_at
                    ? relativeTime(session.last_connected_at)
                    : relativeTime(session.created_at)}
                </div>

                {contextId !== undefined &&
                  session.context_id === contextId &&
                  session.profile_actions_allowed && (
                  <button
                    type="button"
                    aria-label={`Sauvegarder le profil de ${displayName}`}
                    onClick={(event) => void handleSaveProfile(session, event)}
                    disabled={
                      savingProfileIds[session.session_id] === true ||
                      rebootstrappingIds[session.session_id] === true ||
                      session.state === "starting" ||
                      session.state === "stopping" ||
                      session.state === "destroying"
                    }
                    style={{
                      background: "transparent",
                      color: "#fbbf24",
                      border: "1px solid #92400e",
                      borderRadius: 5,
                      padding: "4px 0",
                      fontSize: 10,
                      cursor:
                        savingProfileIds[session.session_id] === true
                          ? "wait"
                          : "pointer",
                      width: "100%",
                    }}
                  >
                    {savingProfileIds[session.session_id] === true
                      ? "Sauvegarde…"
                      : "Sauvegarder le profil"}
                  </button>
                )}

                {contextId !== undefined && session.context_id === contextId &&
                  ["firefox", "chrome"].includes(session.browser ?? "") && session.profile_actions_allowed && (
                  <>
                  <button
                    type="button"
                    data-testid={`vault-rebootstrap-${session.session_id}`}
                    aria-label={`Rétablir le coffre de ${displayName}`}
                    title="Arrête normalement Firefox, conserve son profil et redémarre la même session sur l’image configurée. Un conflit ou une capacité insuffisante bloque la reprise."
                    onClick={(event) => void handleRebootstrapVault(session, event)}
                    disabled={isTransitioning || isThawing ||
                      savingProfileIds[session.session_id] === true ||
                      rebootstrappingIds[session.session_id] === true || selectMode}
                    style={{ background: "transparent", color: "#93c5fd", border: "1px solid #1d4ed8",
                      borderRadius: 5, padding: "4px 0", fontSize: 10, width: "100%" }}
                  >
                    {rebootstrappingIds[session.session_id]
                      ? "Reprise du coffre…"
                      : confirmRebootstrapId === session.session_id
                        ? "Confirmer arrêt et redémarrage"
                        : "Rétablir le coffre"}
                  </button>
                  <button
                    type="button"
                    data-testid={`vault-rebootstrap-status-${session.session_id}`}
                    aria-label={`Consulter la reprise du coffre de ${displayName}`}
                    title="Consultation seule de l’opération serveur ; ne relance ni arrêt ni démarrage."
                    onClick={(event) => void handleObserveVaultRebootstrap(session, event)}
                    disabled={observingRebootstrapIds[session.session_id] === true || selectMode}
                    style={{ background: "transparent", color: "#93c5fd", border: "1px solid #1d4ed8",
                      borderRadius: 5, padding: "4px 0", fontSize: 10, width: "100%" }}
                  >
                    {observingRebootstrapIds[session.session_id] ? "Consultation…" : "Consulter la reprise du coffre"}
                  </button>
                  </>
                )}

                {/* Action button */}
                <button
                  data-testid={`session-connect-${session.session_id}`}
                  onClick={(e) => handleCardAction(session, e)}
                  disabled={isTransitioning || isThawing || rebootstrappingIds[session.session_id] === true}
                  style={{
                    marginTop: "auto",
                    background:
                      isTransitioning || isThawing
                        ? "#374151"
                        : ds === "ongoing"
                          ? "#059669"
                          : ds === "off"
                            ? "#2563eb"
                            : "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 5,
                    padding: "4px 0",
                    fontSize: 11,
                    fontWeight: 500,
                    cursor:
                      isTransitioning || isThawing ? "not-allowed" : "pointer",
                    width: "100%",
                  }}
                >
                  {actionLabel}
                </button>

                <button
                  type="button"
                  data-testid={`session-start-status-${session.session_id}`}
                  aria-label={`Consulter le démarrage de ${displayName}`}
                  onClick={(event) => void handleObserveStart(session.session_id, event)}
                  disabled={observingStartIds[session.session_id] === true || selectMode}
                  style={{ background: "transparent", color: "#93c5fd", border: "1px solid #1d4ed8",
                    borderRadius: 5, padding: "4px 0", fontSize: 10, width: "100%" }}
                >
                  {observingStartIds[session.session_id] ? "Consultation…" : "Consulter le démarrage"}
                </button>

                <button
                  type="button"
                  data-testid={`session-stop-status-${session.session_id}`}
                  aria-label={`Consulter l’arrêt de ${displayName}`}
                  onClick={(event) => void handleObserveStop(session.session_id, event)}
                  disabled={observingStopIds[session.session_id] === true || selectMode}
                  style={{ background: "transparent", color: "#93c5fd", border: "1px solid #1d4ed8",
                    borderRadius: 5, padding: "4px 0", fontSize: 10, width: "100%" }}
                >
                  {observingStopIds[session.session_id] ? "Consultation…" : "Consulter l’arrêt"}
                </button>

                {/* Replay link (T119) */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/sessions/${session.session_id}/replay`);
                  }}
                  style={{
                    background: "transparent",
                    color: "#6b7280",
                    border: "none",
                    fontSize: 10,
                    cursor: "pointer",
                    padding: "2px 0",
                    width: "100%",
                  }}
                >
                  ↻ Replay
                </button>
              </div>
            );
          })}
                </div>
              )}
            </section>
          ))}
        </div>
      )}
      {contextId !== undefined && (
        <AppPicker
          visible={showPicker}
          onClose={() => setShowPicker(false)}
          onSelect={(appName) => {
            setShowPicker(false);
            void handleCreate(appName);
          }}
          creating={creating}
          browserSessionsOnly={contextId !== undefined}
        />
      )}
    </div>
  );
}
